import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { RequestSchemaStatus, RequestSchemaType } from '@liscio/api';
import {
  RequestSectionList,
  RequestDetailsPanel,
  RequestSubDetailsPanel,
  useRequestStore,
  useRequestNavigation,
  useNavigation,
  useGetWorkflowRequest,
} from '@liscio/common';
import { Button, Divider, Stack } from '@liscio/ui';

import { TaxDeliveryRequestMainPanel } from './components';
import { RequestDispatch } from '../../components/RequestEdit/RequestDispatch';
import { useRequestDesktopView } from '../../hooks/useRequestDesktopView';
import { generateRedirectUrl } from 'components/DesktopSiteRedirect/desktop-site-redirect-utils';
import { useTrackWhatToDoTodayDestinationView } from 'modules/dashboard/views/DashboardDesktopView/components/WhatToDoToday/analytics';
import {
  WHAT_TO_DO_TODAY_DASHBOARD_STEPS,
  WHAT_TO_DO_TODAY_DESTINATIONS,
} from 'modules/dashboard/views/DashboardDesktopView/components/WhatToDoToday/constants';
import type {
  ClientPreviewTourCandidate,
  ClientPreviewTourNavigateSignal,
} from 'modules/requests/components/ClientPreviewDialog/clientPreviewTourCandidates';
import type {
  GuidedCustomStepCommand,
  GuidedCustomStepCommandSignal,
} from 'modules/requests/components/ClientPreviewDialog/customStepGuidedDemo';
import RequestContextMenu from 'modules/requests/components/RequestContextMenu/RequestContextMenu';
import { TaxDeliveryTooltipTour } from 'modules/requests/components/TaxDeliveryTooltipTour';
import { shouldShowTaxDeliveryTooltipTour } from 'modules/requests/components/TaxDeliveryTooltipTour/taxDeliveryTooltipTourGating';
import { REQUESTS_BUILDER } from 'modules/requests/route-constants';
import { routes } from 'routes';

const DefaultRequestViewDesktopV5 = React.lazy(() =>
  import('@liscio/common').then((module) => ({
    default: module.DefaultRequestViewDesktopV5,
  }))
);

type RequestDesktopDispatchViewProps = {
  id: string;
};

type WorkflowRequestData = {
  workflowRequest?: {
    workflow?: {
      id?: string | number;
    };
  };
};

export function RequestDesktopDispatchView({
  id,
}: RequestDesktopDispatchViewProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { visibleSections, setDisableEdit, disableEdit, request, requestId } =
    useRequestStore();
  const [taxDeliveryTourOpen, setTaxDeliveryTourOpen] = useState(false);
  const [clientPreviewOpen, setClientPreviewOpen] = useState(false);
  const [openCustomStepDialogSignal, setOpenCustomStepDialogSignal] =
    useState(0);
  const [clientPreviewTourCandidates, setClientPreviewTourCandidates] =
    useState<ClientPreviewTourCandidate[]>([]);
  const [clientPreviewTourNavigateSignal, setClientPreviewTourNavigateSignal] =
    useState<ClientPreviewTourNavigateSignal | null>(null);
  const [guidedCustomStepSavedSignal, setGuidedCustomStepSavedSignal] =
    useState(0);
  const [guidedCustomStepCommandSignal, setGuidedCustomStepCommandSignal] =
    useState<GuidedCustomStepCommandSignal | null>(null);
  const [guidedCustomStepCommandCompletedSignal, setGuidedCustomStepCommandCompletedSignal] =
    useState<GuidedCustomStepCommandSignal | null>(null);
  const [showDashboardReturnAction, setShowDashboardReturnAction] =
    useState(false);

  // Only fetch workflow data if we need it for the Edit Request button
  const shouldFetchWorkflow = useMemo(() => {
    return !!(
      request &&
      request.status === RequestSchemaStatus.draft &&
      request.type === RequestSchemaType.standard
    );
  }, [request]);

  // Conditionally get the workflow data only when needed
  const { data: workflowData } = useGetWorkflowRequest(
    shouldFetchWorkflow ? requestId : undefined
  );

  const { isTaxDeliveryDispatch } = useRequestNavigation();
  const { openUrl } = useNavigation();
  const shouldShowTaxDeliveryTour = useMemo(
    () =>
      shouldShowTaxDeliveryTooltipTour({
        isTaxDeliveryDispatch,
        search: location.search,
        isDismissed: false,
      }),
    [isTaxDeliveryDispatch, location.search]
  );

  useTrackWhatToDoTodayDestinationView({
    search: location.search,
    enabled: isTaxDeliveryDispatch,
    dashboardStep: WHAT_TO_DO_TODAY_DASHBOARD_STEPS.smartTaxDelivery,
    destination: WHAT_TO_DO_TODAY_DESTINATIONS.smartTaxDeliveryDispatch,
    trackingId: id,
    eventProperties: {
      wf_request_uuid: id,
    },
  });

  const {
    rightPanelOpen,
    leftPanelOpen,
    onOpenLeftPanel,
    onCloseLeftPanel,
    onOpenRightPanel,
    onCloseRightPanel,
    handleOnSectionClick,
  } = useRequestDesktopView({
    id,
    rightPanelOpen: true,
    leftPanelOpen: false,
  });

  // Function to handle the Edit Request button click
  const handleEditRequest = () => {
    const workflowId = (workflowData as WorkflowRequestData | undefined)
      ?.workflowRequest?.workflow?.id;

    openUrl(
      generateRedirectUrl(`/${REQUESTS_BUILDER}/${workflowId}`)
    );
  };

  useEffect(() => {
    if (!shouldShowTaxDeliveryTour) {
      return;
    }

    setShowDashboardReturnAction(false);
    setTaxDeliveryTourOpen(true);
  }, [shouldShowTaxDeliveryTour]);

  const handleCloseTaxDeliveryTour = () => {
    setTaxDeliveryTourOpen(false);
    setShowDashboardReturnAction(shouldShowTaxDeliveryTour);
    setGuidedCustomStepSavedSignal(0);
    setGuidedCustomStepCommandSignal(null);
    setGuidedCustomStepCommandCompletedSignal(null);
  };

  const handleBackToDashboard = useCallback(() => {
    setShowDashboardReturnAction(false);
    navigate(routes.dashboard.link());
  }, [navigate]);

  const handleOpenClientPreview = useCallback(() => {
    setClientPreviewOpen(true);
  }, []);

  const handleOpenCustomStepDialog = useCallback(() => {
    setClientPreviewOpen(true);
    setOpenCustomStepDialogSignal((signal) => signal + 1);
  }, []);

  const handleNavigateClientPreviewTourCandidate = useCallback(
    (candidate: ClientPreviewTourCandidate) => {
      setClientPreviewOpen(true);
      setClientPreviewTourNavigateSignal((currentSignal) => ({
        signalId: (currentSignal?.signalId ?? 0) + 1,
        candidate,
      }));
    },
    []
  );

  const handleGuidedCustomStepSaved = useCallback(() => {
    setGuidedCustomStepSavedSignal((signal) => signal + 1);
  }, []);

  const handleGuidedCustomStepCommand = useCallback(
    (command: GuidedCustomStepCommand) => {
      setGuidedCustomStepCommandSignal((currentSignal) => ({
        signalId: (currentSignal?.signalId ?? 0) + 1,
        command,
      }));
    },
    []
  );

  const handleGuidedDemoCommandComplete = useCallback(
    (command: GuidedCustomStepCommand, success = true) => {
      setGuidedCustomStepCommandCompletedSignal((currentSignal) => ({
        signalId: (currentSignal?.signalId ?? 0) + 1,
        command,
        success,
      }));
    },
    []
  );

  return (
    <>
      <DefaultRequestViewDesktopV5
        slotProps={{
          RequestBody: {
            RequestLeftPanel: {
              open: leftPanelOpen,
              toggle: !isTaxDeliveryDispatch,
              onOpen: onOpenLeftPanel,
              onClose: onCloseLeftPanel,
            },
            RequestRightPanel: {
              open: rightPanelOpen,
              toggle: false,
              onOpen: onOpenRightPanel,
              onClose: onCloseRightPanel,
            },
          },
        }}
        slots={{
          RequestMainPanel: isTaxDeliveryDispatch ? (
            <TaxDeliveryRequestMainPanel />
          ) : undefined,
          RequestLeftPanel: !isTaxDeliveryDispatch ? (
            <>
              <RequestSubDetailsPanel />
              <RequestSectionList
                sections={visibleSections}
                onSectionClick={handleOnSectionClick}
              />
            </>
          ) : undefined,
          RequestRightPanel: (
            <Stack maxWidth="40rem" width="40vw">
              <RequestDetailsPanel titleEditable />
              <Divider />
              <RequestDispatch
                open={leftPanelOpen}
                requestId={id}
                width="40vw"
                maxWidth="40rem"
                previewOpen={clientPreviewOpen}
                onPreviewOpenChange={setClientPreviewOpen}
                openCustomStepDialogSignal={openCustomStepDialogSignal}
                onGuidedTourClose={handleCloseTaxDeliveryTour}
                onClientPreviewTourCandidatesChange={setClientPreviewTourCandidates}
                clientPreviewTourNavigateSignal={clientPreviewTourNavigateSignal}
                onGuidedCustomStepSaved={handleGuidedCustomStepSaved}
                guidedCustomStepCommandSignal={guidedCustomStepCommandSignal}
                onGuidedDemoCommandComplete={handleGuidedDemoCommandComplete}
              />
            </Stack>
          ),
          RequestHeaderActions: (
            <>
              {request?.status === RequestSchemaStatus.open && (
                <Button
                  variant="outlined"
                  onClick={() => setDisableEdit(!disableEdit)}
                >
                  {disableEdit ? 'Edit Request' : 'Save Responses'}
                </Button>
              )}
              {request &&
                request.status === RequestSchemaStatus.draft &&
                request.type === RequestSchemaType.standard && (
                  <Button variant="outlined" onClick={handleEditRequest}>
                    Edit Request
                  </Button>
                )}
              <RequestContextMenu />
            </>
          ),
        }}
      />
      {isTaxDeliveryDispatch && (
        <TaxDeliveryTooltipTour
          open={taxDeliveryTourOpen}
          onClose={handleCloseTaxDeliveryTour}
          onOpenClientPreview={handleOpenClientPreview}
          onOpenCustomStepDialog={handleOpenCustomStepDialog}
          clientPreviewTourCandidates={clientPreviewTourCandidates}
          onNavigateClientPreviewTourCandidate={
            handleNavigateClientPreviewTourCandidate
          }
          guidedCustomStepSavedSignal={guidedCustomStepSavedSignal}
          onGuidedCustomStepCommand={handleGuidedCustomStepCommand}
          guidedCustomStepCommandCompletedSignal={
            guidedCustomStepCommandCompletedSignal
          }
          onBackToDashboard={handleBackToDashboard}
          showDashboardReturnAction={showDashboardReturnAction}
        />
      )}
    </>
  );
}
